import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Group, Modal, Stack, Text, Textarea } from "@mantine/core";

import { showLoadingNotification, showSuccessNotification } from "@utils/showNotification";

import { useRejectAppointment } from "@hooks/mutations/appointment/useRejectAppointment";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";


interface RejectAppointmentModalProps {
  opened: boolean;
  close: () => void;
  appointmentId: string;
  onRejected?: () => void;
}

export const RejectAppointmentModal = ({
  opened,
  close,
  appointmentId,
  onRejected,
}: RejectAppointmentModalProps) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const { mutate: rejectAppointment, isLoading } = useRejectAppointment();
  const handleErrors = useQueryErrorHandling();

  const handleClose = () => {
    setReason("");
    close();
  };

  const handleReject = () => {
    const rejectionReason = reason.trim();
    if (!rejectionReason || isLoading) return;

    const id = showLoadingNotification({});
    rejectAppointment(
      { appointmentId, rejectionReason },
      {
        onSuccess: () => {
          showSuccessNotification({
            id,
            title: "success",
            message: "reject-appointment-success",
          });
          handleClose();
          onRejected?.();
        },
        onError: (res: any) => {
          handleErrors(res, "mutation", id);
        },
      }
    );
  };

  return (
    <Modal opened={opened} onClose={handleClose} title={t("reject-request")} centered closeOnClickOutside={!isLoading} closeOnEscape={!isLoading}>
      <Stack>
        <Text size="sm">{t("reject-incoming-request")}</Text>
        <Textarea
          label={t("reason-for-rejection")}
          placeholder={t("type-reason")}
          value={reason}
          onChange={(event) => setReason(event.currentTarget.value)}
          minRows={3}
          required
        />
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={handleClose} disabled={isLoading}>
            {t("cancel")}
          </Button>
          <Button color="red" onClick={handleReject} loading={isLoading} disabled={!reason.trim()}>
            {t("reject-request")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
